import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

interface VenueData {
    id: number;
    venue_name: string;
    capacity: number;
    configuration: string;
    svg_content?: string;
    svg_path?: string;
    image_preview_path?: string;
    sections: any[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared utilities
// ═══════════════════════════════════════════════════════════════════════════════

const SvgInner = React.memo(({ html }: { html: string }) => (
    <div dangerouslySetInnerHTML={{ __html: html }} style={{ width: '100%', height: '100%' }} />
));

function useColorScheme(): boolean {
    const [isDark, setIsDark] = useState<boolean>(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return true;
    });
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isDark;
}

const getCssVars = (dark: boolean): Record<string, string> => ({
    '--bg-page':             dark ? '#111111' : '#f0f4f8',
    '--bg-container':        dark ? '#1a1a1a' : '#ffffff',
    '--bg-svg':              dark ? '#0d0d0d' : '#1a1a1a',
    '--bg-surface':          dark ? '#1e1e1e' : '#f5f7fa',
    '--color-text':          dark ? '#eeeeee' : '#1a1a2e',
    '--color-text-sub':      dark ? '#cccccc' : '#444444',
    '--color-text-muted':    dark ? '#888888' : '#666666',
    '--color-text-faint':    dark ? '#555555' : '#aaaaaa',
    '--color-border':        dark ? '#333333' : '#e0e4ea',
    '--color-border-mid':    dark ? '#444444' : '#c0c8d4',
    '--shadow':              dark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
    '--color-spinner-track': dark ? '#333333' : '#dddddd',
});

// ═══════════════════════════════════════════════════════════════════════════════
// Pan/Zoom hook — viewBox-based, supports mouse wheel, drag-to-pan, touch pinch
// ═══════════════════════════════════════════════════════════════════════════════

interface ViewBox { x: number; y: number; w: number; h: number; }

function usePanZoom(containerRef: React.RefObject<HTMLDivElement | null>, enabled: boolean) {
    const vbRef = useRef<ViewBox>({ x: 0, y: 0, w: 800, h: 600 });
    const origVbRef = useRef<ViewBox>({ x: 0, y: 0, w: 800, h: 600 });
    const isPanningRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });
    const startPosRef = useRef({ x: 0, y: 0 });
    const lastPinchDistRef = useRef(0);

    const applyViewBox = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const svg = el.querySelector('svg');
        if (!svg) return;
        const vb = vbRef.current;
        svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.w} ${vb.h}`);
    }, [containerRef]);

    const initFromSvg = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const svg = el.querySelector('svg');
        if (!svg) return;
        const vbAttr = svg.getAttribute('viewBox');
        if (vbAttr) {
            const [x, y, w, h] = vbAttr.split(/[\s,]+/).map(Number);
            if (!isNaN(w) && w > 0) {
                vbRef.current = { x, y, w, h };
                origVbRef.current = { x, y, w, h };
            }
        }
    }, [containerRef]);

    const screenToSvgScale = useCallback(() => {
        const el = containerRef.current;
        if (!el) return 1;
        const rect = el.getBoundingClientRect();
        return vbRef.current.w / (rect.width || 1);
    }, [containerRef]);

    const zoomAt = useCallback((clientX: number, clientY: number, factor: number) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vb = vbRef.current;
        const scale = screenToSvgScale();
        const svgX = vb.x + (clientX - rect.left) * scale;
        const svgY = vb.y + (clientY - rect.top) * scale;
        const newW = Math.max(10, Math.min(origVbRef.current.w * 10, vb.w * factor));
        const newH = Math.max(10, Math.min(origVbRef.current.h * 10, vb.h * factor));
        vbRef.current = {
            x: svgX - (svgX - vb.x) * (newW / vb.w),
            y: svgY - (svgY - vb.y) * (newH / vb.h),
            w: newW, h: newH,
        };
        applyViewBox();
    }, [containerRef, screenToSvgScale, applyViewBox]);

    const resetZoom = useCallback(() => {
        vbRef.current = { ...origVbRef.current };
        applyViewBox();
    }, [applyViewBox]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || !enabled) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const factor = e.deltaY > 0 ? 1.12 : 0.89;
            zoomAt(e.clientX, e.clientY, factor);
        };

        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            isPanningRef.current = true;
            startPosRef.current = { x: e.clientX, y: e.clientY };
            lastPosRef.current = { x: e.clientX, y: e.clientY };
            el.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isPanningRef.current) return;
            const sc = screenToSvgScale();
            const dx = (e.clientX - lastPosRef.current.x) * sc;
            const dy = (e.clientY - lastPosRef.current.y) * sc;
            vbRef.current.x -= dx;
            vbRef.current.y -= dy;
            lastPosRef.current = { x: e.clientX, y: e.clientY };
            applyViewBox();
        };

        const onPointerUp = (e: PointerEvent) => {
            isPanningRef.current = false;
            el.releasePointerCapture(e.pointerId);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                const tdx = e.touches[0].clientX - e.touches[1].clientX;
                const tdy = e.touches[0].clientY - e.touches[1].clientY;
                lastPinchDistRef.current = Math.hypot(tdx, tdy);
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const tdx = e.touches[0].clientX - e.touches[1].clientX;
                const tdy = e.touches[0].clientY - e.touches[1].clientY;
                const dist = Math.hypot(tdx, tdy);
                if (lastPinchDistRef.current > 0) {
                    const f = lastPinchDistRef.current / dist;
                    const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                    const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                    zoomAt(cx, cy, f);
                }
                lastPinchDistRef.current = dist;
            }
        };

        const onTouchEnd = () => { lastPinchDistRef.current = 0; };

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerUp);
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);

        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('pointercancel', onPointerUp);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [enabled, containerRef, zoomAt, screenToSvgScale, applyViewBox]);

    return useMemo(
        () => ({ initFromSvg, resetZoom, applyViewBox }),
        [initFromSvg, resetZoom, applyViewBox]
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ViewSeatMap Component
// Displays a venue SVG read-only (no section selection).
// Called from a WebView with ?venueId=<id>
// ═══════════════════════════════════════════════════════════════════════════════

const ViewSeatMapComponent: React.FC = () => {
    const appUrl = 'https://maineventbackend.seuytel.com/api/';
    const storageUrl = 'https://maineventbackend.seuytel.com/storage/';
    const isDark = useColorScheme();

    const [searchParams] = useSearchParams();
    // Accept both "venueId" and "venueID" from the URL
    const venueId = searchParams.get('venueId') || searchParams.get('venueID');

    const [venue, setVenue] = useState<VenueData | null>(null);
    const [svgContent, setSvgContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [venuePreviewOpen, setVenuePreviewOpen] = useState(false);

    const svgContainerRef = useRef<HTMLDivElement>(null);
    const venueThumbRef = useRef<HTMLImageElement>(null);

    // ── Responsive ──
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ── Pan/Zoom ──
    const panZoom = usePanZoom(svgContainerRef, !!svgContent);

    // ── Load venue ──
    useEffect(() => {
        if (!venueId) {
            setError('No venue ID provided. Use ?venueId=<id> in the URL.');
            setLoading(false);
            return;
        }
        fetch(`${appUrl}seatmap/get/${encodeURIComponent(venueId)}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to load venue');
                return res.json();
            })
            .then(venueData => {
                const v: VenueData = venueData;
                setVenue(v);
                if (v.svg_content) setSvgContent(v.svg_content);
            })
            .catch(err => setError(err.message || 'Something went wrong'))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId]);

    // ── Build SVG HTML (read-only, no interactive styles) ──
    const svgHtml = useMemo(() => {
        if (!svgContent) return '';
        const styles = `
            .seat-block { cursor: default; }
            .seat { pointer-events: none; }
            .row-label { pointer-events: none; }
        `;
        let result = svgContent.replace(/<svg([^>]*)>/, (_m: string, attrs: string) => {
            let a = attrs;
            if (!a.includes('width=')) a += ' width="100%" height="100%"';
            return `<svg${a}>`;
        });
        if (result.includes('<style>')) {
            return result.replace(/<style>/, `<style>${styles}`);
        }
        return result.replace(/<svg([^>]*)>/, `<svg$1><style>${styles}</style>`);
    }, [svgContent]);

    // ── Init pan/zoom after SVG loads ──
    useEffect(() => {
        const t = setTimeout(() => panZoom.initFromSvg(), 50);
        return () => clearTimeout(t);
    }, [svgHtml, panZoom]);

    // ── Prevent pan-zoom capture on thumbnail ──
    useEffect(() => {
        const thumb = venueThumbRef.current;
        if (!thumb) return;
        const stop = (e: PointerEvent) => e.stopPropagation();
        thumb.addEventListener('pointerdown', stop);
        thumb.addEventListener('pointermove', stop);
        thumb.addEventListener('pointerup', stop);
        return () => {
            thumb.removeEventListener('pointerdown', stop);
            thumb.removeEventListener('pointermove', stop);
            thumb.removeEventListener('pointerup', stop);
        };
    }, [svgContent]);

    const venuePreviewUrl = useMemo(() => {
        if (!venue?.image_preview_path) return '';
        const p = venue.image_preview_path;
        return p.startsWith('http') ? p : `${storageUrl}${p}`;
    }, [venue, storageUrl]);

    const themeVars = getCssVars(isDark);
    const isLargeScreen = windowWidth >= 1024;

    if (loading) {
        return (
            <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <div style={spinnerStyle} />
                    <p style={{ color: 'var(--color-text-muted)', marginTop: 16, fontFamily: FONT_FAMILY }}>Loading venue map...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
                <p style={{ color: '#e74c3c', textAlign: 'center', padding: 20, fontFamily: FONT_FAMILY }}>{error}</p>
            </div>
        );
    }

    return (
        <div style={{ ...wrapperStyle, ...themeVars } as React.CSSProperties}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            {/* ── Card container ── */}
            <div style={{
                width: '100%',
                maxWidth: isLargeScreen ? 900 : '100%',
                height: isLargeScreen ? 'calc(100vh - 20px)' : '100vh',
                background: 'var(--bg-container)',
                borderRadius: isLargeScreen ? 16 : 0,
                overflow: 'hidden',
                boxShadow: 'var(--shadow)',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
            }}>
                {/* Header */}
                <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--color-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    fontFamily: FONT_FAMILY,
                }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>
                        {venue?.venue_name || 'Venue Map'}  | config: {venue?.configuration || 'no configuration'}
                    </h3>
                    <button
                        onClick={() => panZoom.resetZoom()}
                        style={smallBtnStyle}
                        title="Reset zoom"
                    >&#x27F3;</button>
                </div>

                {/* SVG Container */}
                <div
                    ref={svgContainerRef}
                    style={svgContainerStyle}
                >
                    {svgContent ? (
                        <SvgInner html={svgHtml} />
                    ) : (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            color: 'var(--color-text-muted)',
                            fontFamily: FONT_FAMILY,
                        }}>
                            No seat map available for this venue.
                        </div>
                    )}

                    {/* Venue preview thumbnail — bottom-left */}
                    {venuePreviewUrl && (
                        <img
                            ref={venueThumbRef}
                            src={venuePreviewUrl}
                            alt="Venue interior"
                            onClick={(e) => { e.stopPropagation(); setVenuePreviewOpen(true); }}
                            style={{
                                position: 'absolute',
                                bottom: 8,
                                left: 8,
                                width: 50,
                                height: 50,
                                borderRadius: 6,
                                objectFit: 'cover',
                                cursor: 'pointer',
                                border: '2px solid var(--color-border-mid)',
                                zIndex: 10,
                                transition: 'border-color 0.2s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.borderColor = '#3498db')}
                            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border-mid)')}
                        />
                    )}
                </div>

                {/* Venue name badge */}
                {venue?.venue_name && (
                    <div style={{
                        padding: '8px 16px',
                        borderTop: '1px solid var(--color-border)',
                        background: 'var(--bg-surface)',
                        fontSize: 12,
                        color: 'var(--color-text-muted)',
                        textAlign: 'center',
                        flexShrink: 0,
                        fontFamily: FONT_FAMILY,
                    }}>
                        {venue.capacity > 0 ? `Capacity: ${venue.capacity.toLocaleString()}` : ''}
                    </div>
                )}
            </div>

            {/* ── Fullscreen venue preview modal ── */}
            {venuePreviewOpen && venuePreviewUrl && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.96)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                    onClick={() => setVenuePreviewOpen(false)}
                >
                    <button
                        onClick={e => { e.stopPropagation(); setVenuePreviewOpen(false); }}
                        style={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            background: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '50%',
                            color: '#fff',
                            width: 40,
                            height: 40,
                            cursor: 'pointer',
                            fontSize: 18,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                        }}
                    >&#10005;</button>
                    {venuePreviewUrl.match(/\.(mp4|webm)$/i) ? (
                        <video
                            src={venuePreviewUrl}
                            controls
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <img
                            src={venuePreviewUrl}
                            alt="Venue interior"
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════════

const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const wrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'var(--bg-page)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    maxWidth: '100vw',
    fontFamily: FONT_FAMILY,
};

const svgContainerStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'hidden',
    background: 'var(--bg-svg)',
    touchAction: 'none',
    cursor: 'grab',
    position: 'relative',
};

const smallBtnStyle: React.CSSProperties = {
    background: 'none',
    border: '1px solid var(--color-border-mid)',
    borderRadius: 6,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--color-text)',
};

const spinnerStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    border: '3px solid var(--color-spinner-track)',
    borderTop: '3px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
};

export default ViewSeatMapComponent;
